import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  inject,
  input,
  output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { userDtos, webDtos } from '@vectronic/openApi-customer';
import { Item, ItemHelp } from '@vectronic/util';
import { DragScrollComponent, DragScrollModule } from 'ngx-drag-scroll';
import { DetailsItemComponent } from '../../details/details-item/details-item.component';

@Component({
  selector: 'vectronic-deployments-timeline',
  templateUrl: './deployments-timeline.component.html',
  styleUrls: ['./deployments-timeline.component.scss'],
  imports: [
    CommonModule,
    DragScrollModule,
    DetailsItemComponent,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
})
export class DeploymentsTimelineComponent implements AfterViewInit, OnDestroy {
  private router = inject(Router);
  private el = inject(ElementRef);
  collars = input<webDtos.CollarExtended[]>();
  animals = input<webDtos.AnimalExtended[]>();

  hasView = input(true);
  hasEdit = input(true);
  isHorizontal = input(true);

  askCollarPermmision = output<{ target: Item.COLLAR; ids: number[] }>();

  type = input<Item.ANIMAL | Item.COLLAR>(Item.ANIMAL);
  deployments = input<userDtos.Deployment[]>();

  @ViewChild('dragScrollComponent') dragScrollReference: DragScrollComponent;

  selectDeployment = output<{ deployment: webDtos.Deployment; cause: string }>();
  readonly observer = new IntersectionObserver(() => {
    this.onResize();
  });

  get sortedDeployments() {
    return (this.deployments() ?? [])
      .slice()
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  }

  canEdit({ collarId, animalId }: userDtos.Deployment) {
    const collar = this.collars().find(({ id }) => id === collarId);
    const animal = this.animals().find(({ id }) => id === animalId);
    const canEditCollar =
      collar?.role &&
      (collar.role === 'DATA_MANAGER' ||
        collar.role === 'OWNER' ||
        collar.role === 'DEVICE_MANAGER');
    const canEditAnimal = animal?.role && animal.role === 'DATA_MANAGER';
    return canEditCollar || canEditAnimal;
  }

  onLinkToDeployment({ collarId, animalId }: userDtos.Deployment) {
    const id = this.type() === Item.ANIMAL ? collarId : animalId;
    const linkItem = this.type() === Item.ANIMAL ? Item.COLLAR : Item.ANIMAL;

    if (this.type() === Item.ANIMAL) {
      const hasPermission = this.collars().some(({ id }) => id === collarId);
      if (!hasPermission)
        return this.askCollarPermmision.emit({ target: Item.COLLAR, ids: [collarId] });
    }
    this.router.navigate([ItemHelp.toRoute(linkItem, id)]);
  }

  isCurrent({ startDate, endDate }: userDtos.Deployment) {
    const today = new Date();
    return new Date(startDate) <= today && (endDate == null || new Date(endDate) > today);
  }

  onResize() {
    if (this.dragScrollReference) {
      this.dragScrollReference.prevChildrenLength = undefined;
      this.dragScrollReference.ngAfterViewChecked();
    }
  }
  ngAfterViewInit() {
    this.observer.observe(this.el.nativeElement);
  }

  ngOnDestroy() {
    this.observer.unobserve(this.el.nativeElement);
  }
}
